import { type CurrencyListItem, type CurrencyListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { CurrencyFormDrawer } from '../components/CurrencyFormDrawer';
import { listCurrencies } from '../currencies.api';
import {
  useBulkDeleteCurrencies,
  useBulkSetCurrenciesStatus,
  useCurrenciesList,
  useDeleteCurrency,
} from '../use-currencies';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const CurrenciesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteCurrency = useDeleteCurrency();
  const bulkDeleteMut = useBulkDeleteCurrencies();
  const bulkSetStatusMut = useBulkSetCurrenciesStatus();

  const columns: ListScreenColumns<CurrencyListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CurrencyListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'code',
        header: 'Code',
        sortable: true,
        cell: (row) => (
          <span className="font-medium text-zinc-900">
            {row.code}
            {row.symbol ? ` (${row.symbol})` : ''}
          </span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => <span>{row.name}</span>,
      },
      {
        id: 'decimals',
        header: 'Decimals',
        width: '90px',
        cell: (row) => <span>{row.decimals}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CurrencyListItem, CurrencyListQuery>
      title="Currencies"
      description="ISO 4217 currencies used for quotes, bookings, and payments."
      useList={useCurrenciesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add currency' }}
      renderDrawer={({ open, mode, row, close }) => (
        <CurrencyFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          currency={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} currenc${rows.length === 1 ? 'y' : 'ies'}?`,
            description: 'These currencies will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} currenc${rows.length === 1 ? 'y' : 'ies'}?`,
            description: isActive
              ? 'They will become selectable again in quotes, bookings, and payments.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listCurrencies(q),
        filename: 'currencies',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Decimals', value: (r) => r.decimals },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteCurrency.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this currency?',
            description: `${row.code} - ${row.name} will be soft-deleted and hidden from lists.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by code or name (min 3 characters)',
        emptyTitle: 'No currencies match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load currencies',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
